import React from 'react';
/**
 * useCNESConsulta Hook - CNES hospital search data and handlers
 */
import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { listCNESLocal, HospitalCNES } from '../services/cnesService';

// Interface compatible with HospitalCNES API
export interface Hospital {
  cnes: string;
  nome: string;
  razaoSocial: string | null;
  cnpj: string | null;
  endereco: string;
  bairro: string | null;
  cidade: string;
  estado: string;
  cep: string | null;
  telefone: string | null;
  email: string | null;
  tipoEstabelecimento: string | null;
  gestao: string | null;
  leitos: number | null;
}

export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  key: keyof Hospital;
  direction: SortDirection;
}

export interface Filtros {
  nome: string;
  cidade: string;
  estado: string;
  cnes: string;
}

// Helper to convert HospitalCNES to Hospital
const mapHospitalCNESToHospital = (h: HospitalCNES): Hospital => ({
  cnes: h.cnes,
  nome: h.nome,
  razaoSocial: h.nome,
  cnpj: h.cnpj,
  endereco: h.logradouro ? `${h.logradouro}${h.numero ? ', ' + h.numero : ''}` : '',
  bairro: h.bairro,
  cidade: h.municipio,
  estado: h.uf,
  cep: h.cep,
  telefone: h.telefone,
  email: h.email,
  tipoEstabelecimento: h.tipo_estabelecimento,
  gestao: h.gestao,
  leitos: h.qtd_leitos_total
});

export function useCNESConsulta() {
  const [filtros, setFiltros] = useState<Filtros>({
    nome: '',
    cidade: '',
    estado: '',
    cnes: ''
  });
  const [resultados, setResultados] = useState<Hospital[]>([]);
  const [buscaRealizada, setBuscaRealizada] = useState(false);
  const [buscaExata, setBuscaExata] = useState(false);
  const [selectedHospitalDetail, setSelectedHospitalDetail] = useState<Hospital | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [sortConfigs, setSortConfigs] = useState<SortConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const handleBuscar = async () => {
    setLoading(true);
    try {
      const apiFilters: any = { ativo: true };

      if (filtros.nome) {
        apiFilters.search = filtros.nome;
      }
      if (filtros.cidade) {
        apiFilters.municipio = filtros.cidade;
      }
      if (filtros.estado) {
        apiFilters.uf = filtros.estado;
      }
      if (filtros.cnes) {
        apiFilters.search = filtros.cnes;
      }

      const response = await listCNESLocal(apiFilters);
      const hospitaisConvertidos = response.items.map(mapHospitalCNESToHospital);

      setResultados(hospitaisConvertidos);
      setTotalCount(response.count);
      setBuscaRealizada(true);

      if (hospitaisConvertidos.length === 0) {
        toast.error('Nenhum hospital encontrado com os filtros aplicados');
      } else {
        toast.success(`${hospitaisConvertidos.length} hospital(is) encontrado(s)`);
      }
    } catch (error) {
      console.error('Erro ao buscar hospitais:', error);
      toast.error('Erro ao buscar hospitais. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleAtualizar = () => {
    handleBuscar();
    toast.success('Busca atualizada!');
  };

  const handleLimpar = () => {
    setFiltros({ nome: '', cidade: '', estado: '', cnes: '' });
    setResultados([]);
    setBuscaRealizada(false);
    setSortConfigs([]);
  };

  const handleExportar = () => {
    if (resultados.length === 0) {
      toast.error('Nenhum resultado para exportar');
      return;
    }

    const headers = ['CNES', 'Nome', 'Cidade', 'Estado', 'Telefone', 'Email', 'Leitos', 'Tipo', 'Gestão'];
    const csv = [
      headers.join(';'),
      ...resultados.map(h => [
        h.cnes,
        h.nome.replace(/^http:\/\/[^\s]+\s*/, ''),
        h.cidade,
        h.estado,
        h.telefone,
        h.email,
        h.leitos,
        h.tipoEstabelecimento,
        h.gestao
      ].join(';'))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `hospitais_cnes_filtrados_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`${resultados.length} hospital(is) exportado(s)!`);
  };

  const handleExportarTudo = async () => {
    setLoading(true);
    try {
      const response = await listCNESLocal({ ativo: true });
      const todosHospitais = response.items.map(mapHospitalCNESToHospital);

      const headers = ['CNES', 'Nome', 'Cidade', 'Estado', 'Telefone', 'Email', 'Leitos', 'Tipo', 'Gestão'];
      const csv = [
        headers.join(';'),
        ...todosHospitais.map(h => [
          h.cnes,
          h.nome.replace(/^http:\/\/[^\s]+\s*/, ''),
          h.cidade,
          h.estado,
          h.telefone || '',
          h.email || '',
          h.leitos || '',
          h.tipoEstabelecimento || '',
          h.gestao || ''
        ].join(';'))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `hospitais_cnes_completo_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Todos os ${todosHospitais.length} hospitais exportados!`);
    } catch (error) {
      console.error('Erro ao exportar hospitais:', error);
      toast.error('Erro ao exportar hospitais. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (hospital: Hospital) => {
    setSelectedHospitalDetail(hospital);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
  };

  const handleSort = (key: keyof Hospital, event: React.MouseEvent) => {
    if (event.shiftKey) {
      const existingIndex = sortConfigs.findIndex((s) => s.key === key);

      if (existingIndex >= 0) {
        const existing = sortConfigs[existingIndex];
        const newConfig = [...sortConfigs];

        if (existing.direction === 'asc') {
          newConfig[existingIndex] = { key, direction: 'desc' };
        } else {
          newConfig.splice(existingIndex, 1);
        }

        setSortConfigs(newConfig);
      } else {
        if (sortConfigs.length < 3) {
          setSortConfigs([...sortConfigs, { key, direction: 'asc' }]);
        } else {
          setSortConfigs([...sortConfigs.slice(0, 2), { key, direction: 'asc' }]);
        }
      }
    } else {
      const existing = sortConfigs.find((s) => s.key === key);
      const newDirection: SortDirection =
        existing?.direction === 'asc' ? 'desc' : 'asc';

      setSortConfigs([{ key, direction: newDirection }]);
    }
  };

  // Apply sorting to results
  const sortedResultados = useMemo(() => {
    return [...resultados].sort((a, b) => {
      for (const config of sortConfigs) {
        const aVal = a[config.key];
        const bVal = b[config.key];

        let comparison = 0;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          comparison = aVal.localeCompare(bVal);
        } else if (typeof aVal === 'number' && typeof bVal === 'number') {
          comparison = aVal - bVal;
        }

        if (comparison !== 0) {
          return config.direction === 'asc' ? comparison : -comparison;
        }
      }
      return 0;
    });
  }, [resultados, sortConfigs]);

  return {
    // Filter state
    filtros,
    setFiltros,
    buscaExata,
    setBuscaExata,

    // Results
    resultados,
    sortedResultados,
    buscaRealizada,
    totalCount,
    loading,

    // Sorting
    sortConfigs,
    handleSort,

    // Detail modal
    selectedHospitalDetail,
    showDetailModal,
    handleViewDetails,
    closeDetailModal,

    // Actions
    handleBuscar,
    handleAtualizar,
    handleLimpar,
    handleExportar,
    handleExportarTudo,
  };
}
